import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Alert,
    useTheme,
    CircularProgress,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const API_URL = 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/prod';

const ConfirmSignupPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [email, setEmail] = useState(searchParams.get('email') || '');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    // Track how many pending invites the backend reconciled at confirm-time so
    // we can surface a hint on the destination page. (#217 Part 1)
    const [reconciledCount, setReconciledCount] = useState<number>(0);

    // Get redirect URL from query params (default to /explore)
    const redirectUrl = searchParams.get('redirect') || '/home';

    const handleResendCode = async () => {
    if (resendCooldown > 0 || !email) return;
    setResendLoading(true);
    setResendMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/dev'}/auth/resend-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.success) {
        setResendMessage('Code resent! Check your inbox.');
        setResendCooldown(60);
        const timer = setInterval(() => {
          setResendCooldown(prev => {
            if (prev <= 1) { clearInterval(timer); return 0; }
            return prev - 1;
          });
        }, 1000);
      } else {
        setResendMessage(data.error || 'Failed to resend. Please try again.');
      }
    } catch {
      setResendMessage('Network error. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Step 1: Confirm signup
            const confirmResponse = await fetch(`${API_URL}/auth/confirm-signup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, code }),
            });

            const confirmData = await confirmResponse.json();

            if (!confirmData.success) {
                setError(confirmData.error || 'Verification failed');
                setLoading(false);
                return;
            }

            // Capture how many email-only invites the backend reconciled to this
            // user. Used as a hint flag on the redirect URL. (#217 Part 1)
            const reconciled = typeof confirmData.pendingInvitesReconciled === 'number'
                ? confirmData.pendingInvitesReconciled
                : 0;
            setReconciledCount(reconciled);

            // Step 2: Show success and prompt for password to auto-login
            trackEvent('signup_completed');
            setSuccess(true);
            setShowPassword(true);

        } catch (err) {
            setError('Failed to verify. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const loginResponse = await fetch(`${API_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });

            const loginData = await loginResponse.json();

            if (loginData.success) {
                localStorage.setItem('accessToken', loginData.tokens.accessToken);
                localStorage.setItem('idToken', loginData.tokens.idToken);
                localStorage.setItem('refreshToken', loginData.tokens.refreshToken);
                localStorage.setItem('user', JSON.stringify(loginData.user));

                // Append a fromSignup hint when redirecting to /friends so the
                // landing page can prime the user with a "you have a pending
                // invite — click Accept" cue. The hint also encodes how many
                // invites were reconciled, in case future UX wants to vary copy.
                // (#217 Part 1 — manual-click preserved per Q1, this just primes)
                let finalRedirect = redirectUrl;
                if (redirectUrl.includes('/friends') && reconciledCount > 0) {
                    finalRedirect = `/home?fromInvite=1&reconciled=${reconciledCount}`;
                }

                // Navigate to redirect URL or default to /explore
                navigate(finalRedirect);
            } else {
                setError(loginData.error || 'Login failed');
            }
        } catch (err) {
            setError('Failed to login. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Determine button text based on redirect destination
    const getButtonText = () => {
        if (loading) return <CircularProgress size={24} />;
        if (redirectUrl.includes('/friends')) return 'Continue to Friends';
        if (redirectUrl.includes('/community')) return 'Continue to Community';
        if (redirectUrl.includes('/trips')) return 'Continue to Trips';
        return 'Continue to Dashboard';
    };

    if (success && showPassword) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: theme.palette.background.default,
                }}
            >
                <Container maxWidth="sm">
                    <Card sx={{ borderRadius: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                        <CardContent sx={{ p: 4, textAlign: 'center' }}>
                            <CheckCircleOutlineIcon
                                sx={{ fontSize: '4rem', color: theme.palette.primary.main, mb: 2 }}
                            />
                            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Email Verified!
                            </Typography>
                            <Typography sx={{ color: 'text.secondary', mb: 4 }}>
                                Enter your password to continue
                            </Typography>

                            {/* If invites were reconciled, prime the user that they'll
                                see a pending invite when they land. (#217 Part 1) */}
                            {reconciledCount > 0 && redirectUrl.includes('/friends') && (
                                <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                                    You have {reconciledCount === 1 ? 'a pending friend invitation' : `${reconciledCount} pending friend invitations`} waiting. After login, click <strong>Accept</strong> to connect.
                                </Alert>
                            )}

                            {error && (
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    {error}
                                </Alert>
                            )}

                            <form onSubmit={handleLogin}>
                                <TextField
                                    fullWidth
                                    label="Password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    sx={{ mb: 3 }}
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    disabled={loading}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: '30px',
                                        fontWeight: 'bold',
                                        textTransform: 'none',
                                        fontSize: '1.1rem',
                                    }}
                                >
                                    {getButtonText()}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </Container>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: theme.palette.background.default,
            }}
        >
            <Container maxWidth="sm">
                <Card sx={{ borderRadius: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                    <CardContent sx={{ p: 4 }}>
                        <Typography
                            variant="h4"
                            sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}
                        >
                            Verify Your Email
                        </Typography>
                        <Typography
                            sx={{ color: 'text.secondary', mb: 4, textAlign: 'center' }}
                        >
                            Enter the 6-digit code we sent to your email
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {error}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                sx={{ mb: 3 }}
                            />
                            <TextField
                                fullWidth
                                label="Verification Code"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                required
                                placeholder="Enter 6-digit code"
                                inputProps={{ maxLength: 6 }}
                                sx={{ mb: 3 }}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                size="large"
                                disabled={loading}
                                sx={{
                                    py: 1.5,
                                    borderRadius: '30px',
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                }}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Verify Email'}
                            </Button>
                        </form>

                        <Typography
                            sx={{ mt: 3, textAlign: 'center', color: 'text.secondary' }}
                        >
                            Didn't receive the code?{' '}
                            <Button
                                variant="text"
                                onClick={handleResendCode}
                                disabled={resendCooldown > 0 || resendLoading}
                                sx={{ textTransform: 'none', fontWeight: 'bold', color: '#2D6E6A' }}
                            >
                                {resendLoading ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                            </Button>
                        </Typography>
                        {resendMessage && (
                          <Typography variant="caption" sx={{ color: resendMessage.includes('resent') ? '#2D6E6A' : '#e53935', display: 'block', textAlign: 'center', mt: 1 }}>
                            {resendMessage}
                          </Typography>
                        )}
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default ConfirmSignupPage;
